import React, { useContext } from "react"
import { GetStaticProps, NextPage } from "next"
import Head from "next/head"
import { isEmpty } from "lodash"
import { Breadcrumb, Container } from "react-bootstrap"
import { useCookies } from "react-cookie"
import Header from "@modules/Common/components/Header"
import Page from "@modules/Common/components/ClientPage"
import { AppContext } from "AppContext"
import StudentUpcomingWorkShops from "@modules/Workshop/components/StudentUpcomingWorkShops"
import StudentRegisteredWorkShops from "@modules/Workshop/components/StudentRegisteredWorkShops"
import StudentPastWorkShops from "@modules/Workshop/components/StudentPastWorkShops"
import { initializeApollo } from "apollo/client"
import PublicUpcomingWorkShops from "@modules/Workshop/components/PublicUpcomingWorkShops"
import PublicPastWorkShops from "@modules/Workshop/components/PublicPastWorkShops"
import { DEFAULT_META_IMAGE_URL } from "@utils/constant"
import { PublicSsrWorkshopsDocument, PublicSsrWorkshopsQuery, PublicSsrWorkshopsQueryVariables } from "@generated/graphql"
import Link from "next/link"

interface IWorkshops {
  upcomingWorkshops?: PublicSsrWorkshopsQuery["publicUpcomingWorkshops"]
  pastWorkshops?: PublicSsrWorkshopsQuery["publicPastWorkshops"]
}

const WorkshopsIndex: NextPage<IWorkshops> = ({ upcomingWorkshops, pastWorkshops }) => {

  const { state } = useContext(AppContext)
  const [cookies] = useCookies(["idToken"])

  const title = "Explore Workshops, Courses and Projects for kids and adults | MySkillShaala"
  const description = "Looking for interesting workshops, courses, and projects for kids and adults. We believe that learning should be fun, and so we curate programs that are relevant, useful, and affordable. Explore programs here."
  const image = DEFAULT_META_IMAGE_URL

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name="title" content={title} />
        <meta name="description" content={description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${process.env.NEXT_PUBLIC_SITE_URL}/workshops`} />
        <meta property="og:site_name" content={process.env.NEXT_PUBLIC_SITE_URL} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={image} />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={`${process.env.NEXT_PUBLIC_SITE_URL}/workshops`} />
        <meta property="twitter:title" content={title} />
        <meta property="twitter:description" content={description} />
        <meta property="twitter:image" content={image} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [{
              "@type": "ListItem",
              "position": 1,
              "name": "Skills",
              "item": `${process.env.NEXT_PUBLIC_SITE_URL}/workshops`
            }]
          })
        }} />
      </Head>
      <Page>
        <Header />
        <Container>
          <Breadcrumb>
            <Breadcrumb.Item linkAs={Link} href="/dashboard">Home</Breadcrumb.Item>
            <Breadcrumb.Item linkAs={Link} href="/workshops">Workshops</Breadcrumb.Item>
          </Breadcrumb>
          {cookies.idToken === undefined && isEmpty(state.user) &&
            <>
              {upcomingWorkshops && <PublicUpcomingWorkShops workshops={upcomingWorkshops} />}
              {pastWorkshops && <PublicPastWorkShops workshops={pastWorkshops} />}
            </>
          }
          {!isEmpty(state.user) &&
            <>
              <StudentRegisteredWorkShops />
              <StudentUpcomingWorkShops />
              <StudentPastWorkShops />
            </>
          }
        </Container>
      </Page>
    </>
  )
}

export const getStaticProps: GetStaticProps = async () => {
  const apolloClient = initializeApollo()
  const { data } = await apolloClient.query<PublicSsrWorkshopsQuery, PublicSsrWorkshopsQueryVariables>({
    query: PublicSsrWorkshopsDocument,
    variables: {
      skip: 0,
      take: 8
    }
  })
  return {
    props: {
      upcomingWorkshops: data.publicUpcomingWorkshops,
      pastWorkshops: data.publicPastWorkshops,
      initialApolloState: apolloClient.cache.extract(),
    },
    revalidate: 600
  }
}

export default WorkshopsIndex
